/**
 * Environment configuration, validated once at module load.
 *
 * Mirrors the backend's fail-fast config module: a missing or malformed
 * variable should stop the app immediately with a clear message, not surface
 * three screens later as `fetch("undefined/v1/players")`.
 */

interface AppEnv {
  apiBaseUrl: string;
  appName: string;
  sentryDsn: string | null;
  isDev: boolean;
}

function requireVar(name: string, value: string | undefined): string {
  if (value === undefined || value.trim() === '') {
    throw new Error(
      `Missing required environment variable ${name}. Copy .env.example to .env and fill it in.`,
    );
  }
  return value.trim();
}

function requireUrl(name: string, value: string | undefined): string {
  const raw = requireVar(name, value);
  try {
    new URL(raw);
  } catch {
    throw new Error(`Environment variable ${name} must be a valid URL. Received: "${raw}"`);
  }
  return raw.replace(/\/$/, '');
}

export const env: AppEnv = {
  apiBaseUrl: requireUrl('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
  appName: requireVar('VITE_APP_NAME', import.meta.env.VITE_APP_NAME),
  sentryDsn: import.meta.env.VITE_SENTRY_DSN?.trim() || null,
  isDev: import.meta.env.DEV,
};
